import React, {CSSProperties, useEffect, useRef, useState} from 'react';

interface OdxDropdownProps {
    content: string | React.ReactNode;
    position: 'bottom' | 'top' | 'right' | 'left' | 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end' | 'right-start' | 'right-end' | 'left-start' | 'left-end';
    matchReferenceWidth?: boolean;
    offset?: number;
    showArrow: boolean;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    buttonRef: React.RefObject<HTMLButtonElement> | React.RefObject<HTMLDivElement>;
}

/**
 * Dropdown component. Is used to display a dropdown.
 * @param content The content to display in the dropdown.
 * @param position Position where to display the dropdown in relation to the button.
 * @param matchReferenceWidth Whether the dropdown should match the width of the reference element.
 * @param offset Offset to apply to the dropdown position.
 * @param showArrow Whether to show an arrow on the dropdown.
 * @param isOpen Whether the dropdown is open.
 * @param setIsOpen Function to call when the dropdown should be opened or closed.
 * @param buttonRef Reference to the button that opens the dropdown.
 * @constructor
 */
const OdxDropdown: React.FC<OdxDropdownProps> = ({
                                                     content,
                                                     position,
                                                     matchReferenceWidth = false,
                                                     offset = 0,
                                                     showArrow,
                                                     isOpen,
                                                     setIsOpen,
                                                     buttonRef
                                                 }) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [arrowStyles, setArrowStyles] = useState<CSSProperties>({});
    const [dropdownStyles, setDropdownStyles] = useState<CSSProperties>({});

    const calculatePositions = () => {
        if (dropdownRef.current && buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const dropdownRect = dropdownRef.current.getBoundingClientRect();
            const newArrowStyles: CSSProperties = {};
            const newDropdownStyles: CSSProperties = {
                position: 'fixed',
                zIndex: 1000,
            };

            if (matchReferenceWidth) {
                newDropdownStyles.width = `${buttonRect.width}px`;
            }

            switch (position) {
                case 'bottom':
                    newDropdownStyles.top = `${buttonRect.top + buttonRect.height + offset}px`;
                    if (matchReferenceWidth) {
                        newDropdownStyles.left = `${buttonRect.left}px`;
                    } else {
                        newDropdownStyles.left = `${buttonRect.left - dropdownRect.width / 2 + buttonRect.width / 2}px`;
                    }
                    newArrowStyles.left = `${dropdownRect.width / 2 - 5}px`;
                    newArrowStyles.top = `-6px`;
                    break;
                case 'top':
                    newDropdownStyles.top = `${buttonRect.top - dropdownRect.height - offset}px`;
                    if (matchReferenceWidth) {
                        newDropdownStyles.left = `${buttonRect.left}px`;
                    } else {
                        newDropdownStyles.left = `${buttonRect.left - dropdownRect.width / 2 + buttonRect.width / 2}px`;
                    }
                    newArrowStyles.left = `${dropdownRect.width / 2 - 5}px`;
                    newArrowStyles.bottom = `-6px`;
                    break;
                case 'right':
                    if (showArrow) {
                        newDropdownStyles.left = `${buttonRect.left + buttonRect.width + offset + 5}px`;
                    } else {
                        newDropdownStyles.left = `${buttonRect.left + buttonRect.width + offset}px`;
                    }
                    newDropdownStyles.top = `${buttonRect.top + buttonRect.height / 2 - dropdownRect.height / 2}px`;
                    newArrowStyles.top = `${dropdownRect.height / 2 - 5}px`;
                    newArrowStyles.left = `-6px`;
                    break;
                case 'left':
                    if (showArrow) {
                        newDropdownStyles.left = `${buttonRect.left - dropdownRect.width - offset - 5}px`;
                    } else {
                        newDropdownStyles.left = `${buttonRect.left - dropdownRect.width - offset}px`;
                    }
                    newDropdownStyles.top = `${buttonRect.top + buttonRect.height / 2 - dropdownRect.height / 2}px`;
                    newArrowStyles.top = `${dropdownRect.height / 2 - 5}px`;
                    newArrowStyles.right = `-6px`;
                    break;
                case 'bottom-start':
                    newDropdownStyles.top = `${buttonRect.top + buttonRect.height + offset}px`;
                    newDropdownStyles.left = `${buttonRect.left}px`;
                    newArrowStyles.left = `${buttonRect.width / 2 - 5}px`;
                    newArrowStyles.top = `-6px`;
                    break;
                case 'bottom-end':
                    newDropdownStyles.top = `${buttonRect.top + buttonRect.height + offset}px`;
                    newDropdownStyles.left = `${buttonRect.left + buttonRect.width - dropdownRect.width}px`;
                    newArrowStyles.left = `${dropdownRect.width - buttonRect.width / 2 - 5}px`;
                    newArrowStyles.top = `-6px`;
                    break;
                case 'top-start':
                    newDropdownStyles.top = `${buttonRect.top - dropdownRect.height - offset}px`;
                    newDropdownStyles.left = `${buttonRect.left}px`;
                    newArrowStyles.left = `${buttonRect.width / 2 - 5}px`;
                    newArrowStyles.bottom = `-6px`;
                    break;
                case 'top-end':
                    newDropdownStyles.top = `${buttonRect.top - dropdownRect.height - offset}px`;
                    newDropdownStyles.left = `${buttonRect.left + buttonRect.width - dropdownRect.width}px`;
                    newArrowStyles.left = `${dropdownRect.width - buttonRect.width / 2 - 5}px`;
                    newArrowStyles.bottom = `-6px`;
                    break;
                case 'right-start':
                    newDropdownStyles.left = `${buttonRect.left + buttonRect.width + offset}px`;
                    newDropdownStyles.top = `${buttonRect.top}px`;
                    newArrowStyles.top = `${buttonRect.height / 2 - 5}px`;
                    newArrowStyles.left = `-6px`;
                    break;
                case 'right-end':
                    newDropdownStyles.left = `${buttonRect.left + buttonRect.width + offset}px`;
                    newDropdownStyles.top = `${buttonRect.top + buttonRect.height - dropdownRect.height}px`;
                    newArrowStyles.top = `${dropdownRect.height - buttonRect.height / 2 - 5}px`;
                    newArrowStyles.left = `-6px`;
                    break;
                case 'left-start':
                    newDropdownStyles.left = `${buttonRect.left - dropdownRect.width - offset}px`;
                    newDropdownStyles.top = `${buttonRect.top}px`;
                    newArrowStyles.top = `${buttonRect.height / 2 - 5}px`;
                    newArrowStyles.right = `-6px`;
                    break;
                case 'left-end':
                    newDropdownStyles.left = `${buttonRect.left - dropdownRect.width - offset}px`;
                    newDropdownStyles.top = `${buttonRect.top + buttonRect.height - dropdownRect.height}px`;
                    newArrowStyles.top = `${dropdownRect.height - buttonRect.height / 2 - 5}px`;
                    newArrowStyles.right = `-6px`;
                    break;
            }
            setArrowStyles(newArrowStyles);
            setDropdownStyles(newDropdownStyles);
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [buttonRef, setIsOpen, dropdownRef]);

    useEffect(() => {
        if (isOpen) {
            calculatePositions();
        }
    }, [isOpen]);

    useEffect(() => {
        calculatePositions();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            calculatePositions();
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize);

        // Check for other scrollable containers
        const scrollableContainers = document.querySelectorAll('[data-scrollable]');
        scrollableContainers.forEach(container => {
            container.addEventListener('scroll', handleResize);
        });

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize);
            scrollableContainers.forEach(container => {
                container.removeEventListener('scroll', handleResize);
            });
        };
    }, [isOpen]);

    return (
        <>
            {(
                <div className={`odx-cdk-connected-overlay !opacity-100 ${!isOpen && 'hidden'}  z-10`}
                     style={dropdownStyles} ref={dropdownRef}>
                    <div className="odx-cdk-connected-overlay__content odx-dropdown-overlay">
                        <div className="odx-dropdown">
                            <div tabIndex={0} className="cdk-visually-hidden cdk-focus-trap-anchor"
                                 aria-hidden="true"></div>
                            <div className="odx-dropdown__inner">
                                {content}
                            </div>
                            <div tabIndex={0} className="cdk-visually-hidden cdk-focus-trap-anchor"
                                 aria-hidden="true"></div>
                        </div>
                        {showArrow && <div className="odx-cdk-connected-overlay-arrow" style={arrowStyles}></div>}
                    </div>
                </div>
            )}
        </>
    );
};

export default OdxDropdown;